import { useState, useEffect } from "react";
import "@/App.css";
import { 
  Menu, 
  X, 
  Star, 
  MapPin, 
  Clock, 
  Phone, 
  Coffee, 
  ChevronRight,
  MessageCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

// Constants
const WHATSAPP_NUMBER = "+6285393429558";
const GOOGLE_MAPS_LINK = "https://maps.app.goo.gl/Rfui3e21GP3kTihE7";

// Images
const IMAGES = {
  logo: "https://customer-assets.emergentagent.com/job_uncle-cafe-minimal/artifacts/87tphmkc_images.jpg",
  hero: "https://images.pexels.com/photos/15484160/pexels-photo-15484160.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1080&w=1920",
  about: "https://images.pexels.com/photos/2174069/pexels-photo-2174069.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=800&w=800",
  food: [
    "https://customer-assets.emergentagent.com/job_uncle-cafe-minimal/artifacts/qv8apngz_unnamed%20%281%29.webp",
    "https://customer-assets.emergentagent.com/job_uncle-cafe-minimal/artifacts/9q3whf4i_unnamed%20%284%29.webp",
    "https://customer-assets.emergentagent.com/job_uncle-cafe-minimal/artifacts/n3gj481h_unnamed.webp",
  ]
};

// Menu Data
const MENU_DATA = {
  breakfast: [
    { name: "Avocado Toast", desc: "Creamy Mashed Avocado, Egg, Chilli Curry Sauce, Cheese, Salad", price: "55K" },
    { name: "English Breakfast", desc: "Sunny side up, Sausage, Smoked Beef, Mushroom, Baked Beans", price: "55K" },
    { name: "Egg Benedict", desc: "Poached Egg, Hollandaise Sauce", price: "55K" },
    { name: "French Toast Banana", desc: "Classic French Toast with Caramel Banana & Cream Custard", price: "40K" },
    { name: "Japanese Pancake", desc: "Fluffy Pancake Served with Cream Custard", price: "45K" },
    { name: "Kaya Toast", desc: "Classic Bread, Coconut Jam, Butter", price: "40K" },
    { name: "Omelette & Sausage", desc: "Two Eggs, Sausage & French Fries", price: "45K" },
    { name: "Mantau Beef Blackpepper", desc: "4 Steamed Mantau Beef Blackpepper", price: "45K" },
  ],
  indonesian: [
    { name: "Chicken Congee / Bubur Ayam", desc: "Served with Ceker, Tongkol, Salted Egg, Chicken", price: "35K" },
    { name: "Kwetiau Goreng", desc: "Flat Rice Noodles, Shrimps, Meatball, Sprouts, Spring Onion", price: "45K" },
  ],
  coffee: [
    { name: "Espresso", desc: "Espresso & Infuse Water", price: "15K" },
    { name: "Barista Breakfast", desc: "Espresso, Cappuccino & Infuse Water", price: "50K" },
    { name: "Magic Latte", desc: "Double Shot Ristretto & Steamed Milk", price: "30K" },
    { name: "Americano", desc: "Free Americano: 07.00 AM - 08.00 AM", price: "25K" },
  ],
  tea: [
    { name: "Chrysanthemum Flower Tea", desc: "Premium flower tea", price: "30K" },
    { name: "Jasmine Flower Tea", desc: "Aromatic jasmine tea", price: "30K" },
    { name: "Rosella Flower Tea", desc: "Refreshing rosella tea", price: "30K" },
    { name: "Hot Tea", desc: "All In Refill!", price: "10K" },
  ]
};

// Testimonials
const TESTIMONIALS = [
  {
    name: "Rizky A.",
    text: "Tempat hangout terbaik di Tarakan! Kopinya enak dan tempatnya nyaman banget untuk kerja atau ngobrol santai.",
    rating: 5
  },
  {
    name: "Sarah M.",
    text: "Menu breakfast-nya recommended banget, especially English Breakfast. Harga terjangkau dengan porsi yang pas.",
    rating: 5
  },
  {
    name: "Dian P.",
    text: "Suasananya cozy, WiFi kencang, dan pelayanannya ramah. Pasti balik lagi!",
    rating: 4
  }
];

// Navigation Component
const NavBar = ({ isScrolled, toggleMobileMenu, isMobileMenuOpen }) => (
  <header 
    className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'glass-header' : 'bg-transparent'
    }`}
    data-testid="navigation"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="flex items-center justify-between h-20">
        {/* Logo */}
        <a href="#hero" className="flex-shrink-0" data-testid="nav-logo">
          <img 
            src={IMAGES.logo} 
            alt="The Uncle Coffee & Kitchen" 
            className="h-12 w-auto"
          />
        </a>
        
        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-8">
          <a href="#about" className="nav-link" data-testid="nav-about">About</a>
          <a href="#menu" className="nav-link" data-testid="nav-menu">Menu</a>
          <a href="#testimonials" className="nav-link" data-testid="nav-testimonials">Reviews</a>
          <a href="#location" className="nav-link" data-testid="nav-location">Location</a>
        </nav>
        
        {/* CTA Button */}
        <a 
          href={`https://wa.me/${WHATSAPP_NUMBER.replace(/\+/g, '')}`}
          target="_blank"
          rel="noopener noreferrer"
          className="hidden md:inline-flex btn-primary text-sm"
          data-testid="nav-whatsapp-btn"
        >
          <MessageCircle size={16} />
          Hubungi Kami
        </a>
        
        {/* Mobile Menu Toggle */}
        <button 
          className="md:hidden text-white p-2"
          onClick={toggleMobileMenu}
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>
    </div>
  </header>
);

// Mobile Menu Component
const MobileMenu = ({ isOpen, onClose }) => {
  if (!isOpen) return null;
  
  return (
    <div className="mobile-menu" data-testid="mobile-menu">
      <button 
        onClick={onClose} 
        className="absolute top-6 right-6 text-white z-[110] p-2 hover:text-gold transition-colors"
        aria-label="Close menu"
        data-testid="mobile-menu-close"
      >
        <X size={32} />
      </button>
      <a href="#about" onClick={onClose} className="text-gold">About</a>
      <a href="#menu" onClick={onClose}>Menu</a>
      <a href="#testimonials" onClick={onClose}>Reviews</a>
      <a href="#location" onClick={onClose}>Location</a>
      <a 
        href={`https://wa.me/${WHATSAPP_NUMBER.replace(/\+/g, '')}`}
        target="_blank"
        rel="noopener noreferrer"
        className="btn-primary mt-4"
        onClick={onClose}
      >
        <MessageCircle size={20} />
        Hubungi Kami
      </a>
    </div>
  );
};

// Hero Section
const HeroSection = () => (
  <section 
    id="hero" 
    className="relative min-h-screen flex items-center justify-center"
    data-testid="hero-section"
  >
    {/* Background Image */}
    <div className="absolute inset-0">
      <img 
        src={IMAGES.hero}
        alt="The Uncle Coffee & Kitchen Atmosphere"
        className="w-full h-full object-cover img-treatment"
      />
      <div className="hero-overlay absolute inset-0" />
    </div>
    
    {/* Content */}
    <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
      <span 
        className="text-sm font-body uppercase tracking-[0.2em] text-gold opacity-0 animate-fadeInUp"
        style={{ animationDelay: '0.2s', animationFillMode: 'forwards' }}
      >
        EST. 2018
      </span>
      <h1 
        className="font-heading text-4xl sm:text-5xl lg:text-7xl text-white mt-4 mb-6 opacity-0 animate-fadeInUp"
        style={{ animationDelay: '0.4s', animationFillMode: 'forwards' }}
      >
        Nikmati Setiap Momen<br />
        <span className="text-gold">dalam Secangkir Kopi</span>
      </h1>
      <p 
        className="text-lg md:text-xl text-gray-300 mb-8 max-w-2xl mx-auto opacity-0 animate-fadeInUp"
        style={{ animationDelay: '0.6s', animationFillMode: 'forwards' }}
      >
        Tempat hangout terbaik di Tarakan. Coffee, Breakfast, dan suasana yang nyaman untuk semua momen spesialmu.
      </p>
      <div 
        className="flex flex-col sm:flex-row gap-4 justify-center opacity-0 animate-fadeInUp"
        style={{ animationDelay: '0.8s', animationFillMode: 'forwards' }}
      >
        <a href="#menu" className="btn-primary" data-testid="hero-menu-btn">
          <Coffee size={20} />
          Lihat Menu
        </a>
        <a 
          href={GOOGLE_MAPS_LINK}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-secondary"
          data-testid="hero-directions-btn"
        >
          <MapPin size={20} />
          Petunjuk Arah
        </a>
      </div>
    </div>
    
    {/* Scroll indicator */}
    <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
      <ChevronRight size={32} className="text-gold rotate-90" />
    </div>
  </section>
);

// About Section
const AboutSection = () => (
  <section 
    id="about" 
    className="py-24 md:py-32 bg-[#0A0A0A]"
    data-testid="about-section"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 items-center">
        {/* Text Content */}
        <div className="md:col-span-5 order-2 md:order-1">
          <span className="text-sm font-body uppercase tracking-[0.2em] text-gold">
            About Us
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-gold mt-4 mb-6">
            Your Cozy<br />Hangout Spot
          </h2>
          <p className="text-lg text-gray-400 leading-relaxed mb-6">
            Sejak 2018, The Uncle Coffee & Kitchen telah menjadi tempat favorit warga Tarakan untuk menikmati kopi berkualitas, hidangan lezat, dan suasana yang hangat.
          </p>
          <p className="text-lg text-gray-400 leading-relaxed mb-8">
            Kami menyajikan berbagai pilihan menu breakfast, hidangan Indonesia, hingga aneka kopi dan teh pilihan. Semua disajikan dengan penuh kehangatan dan cinta.
          </p>
          <div className="flex items-center gap-6">
            <div className="text-center">
              <span className="text-3xl font-heading text-gold">4.4</span>
              <div className="flex items-center gap-1 mt-1">
                {[...Array(5)].map((_, i) => (
                  <Star 
                    key={i} 
                    size={14} 
                    className={i < 4 ? "star-filled fill-current" : "star-empty"} 
                  />
                ))}
              </div>
              <span className="text-xs text-gray-500 mt-1 block">Google Reviews</span>
            </div>
            <div className="w-px h-12 bg-gold/30" />
            <div className="text-center">
              <span className="text-3xl font-heading text-gold">7+</span>
              <span className="text-xs text-gray-500 mt-1 block">Tahun Melayani</span>
            </div>
          </div>
        </div>
        
        {/* Image */}
        <div className="md:col-span-7 order-1 md:order-2">
          <div className="relative">
            <img 
              src={IMAGES.about}
              alt="The Uncle Coffee & Kitchen Interior"
              className="w-full h-[400px] md:h-[500px] object-cover img-treatment"
            />
            <div className="absolute -bottom-4 -left-4 md:-bottom-6 md:-left-6 bg-[#151515] border border-gold/30 p-4 md:p-6">
              <span className="text-gold font-heading text-xl md:text-2xl block">Tarakan</span>
              <span className="text-gray-400 text-sm">Kalimantan Utara</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Menu Item Component
const MenuItem = ({ name, desc, price }) => (
  <div className="flex justify-between items-start py-4 border-b border-white/10 last:border-0 group">
    <div className="flex-1 pr-4">
      <h4 className="font-heading text-lg text-white group-hover:text-gold transition-colors">
        {name}
      </h4>
      <p className="text-sm text-gray-500 mt-1">{desc}</p>
    </div>
    <span className="price-tag flex-shrink-0">Rp {price}</span>
  </div>
);

// Menu Section
const MenuSection = () => (
  <section 
    id="menu" 
    className="py-24 md:py-32 bg-[#151515]"
    data-testid="menu-section"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      {/* Header */}
      <div className="text-center mb-12">
        <span className="text-sm font-body uppercase tracking-[0.2em] text-gold">
          Our Menu
        </span>
        <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-gold mt-4 mb-4">
          Menu Highlights
        </h2>
        <p className="text-lg text-gray-400 max-w-2xl mx-auto">
          Nikmati berbagai pilihan menu berkualitas dengan harga terjangkau
        </p>
        <div className="inline-block bg-gold/20 text-gold font-semibold px-6 py-2 mt-4">
          Rp 15K - 55K
        </div>
      </div>
      
      {/* Food Gallery */}
      <div className="grid grid-cols-3 gap-3 md:gap-4 mb-12">
        {IMAGES.food.map((img, idx) => (
          <div key={idx} className="aspect-square overflow-hidden">
            <img 
              src={img} 
              alt={`Food item ${idx + 1}`}
              className="w-full h-full object-cover img-treatment hover:scale-110 transition-transform duration-500"
            />
          </div>
        ))}
      </div>
      
      {/* Menu Tabs */}
      <Tabs defaultValue="breakfast" className="w-full">
        <TabsList className="w-full flex flex-wrap justify-center gap-2 bg-transparent mb-8">
          <TabsTrigger 
            value="breakfast" 
            className="category-tab data-[state=active]:text-gold data-[state=active]:border-gold"
            data-testid="tab-breakfast"
          >
            Breakfast
          </TabsTrigger>
          <TabsTrigger 
            value="indonesian" 
            className="category-tab data-[state=active]:text-gold data-[state=active]:border-gold"
            data-testid="tab-indonesian"
          >
            Indonesian
          </TabsTrigger>
          <TabsTrigger 
            value="coffee" 
            className="category-tab data-[state=active]:text-gold data-[state=active]:border-gold"
            data-testid="tab-coffee"
          >
            Coffee
          </TabsTrigger>
          <TabsTrigger 
            value="tea" 
            className="category-tab data-[state=active]:text-gold data-[state=active]:border-gold"
            data-testid="tab-tea"
          >
            Tea
          </TabsTrigger>
        </TabsList>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12">
          <TabsContent value="breakfast" className="mt-0 col-span-1 md:col-span-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12">
              <div>
                {MENU_DATA.breakfast.slice(0, 4).map((item, idx) => (
                  <MenuItem key={idx} {...item} />
                ))}
              </div>
              <div>
                {MENU_DATA.breakfast.slice(4).map((item, idx) => (
                  <MenuItem key={idx} {...item} />
                ))}
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="indonesian" className="mt-0 col-span-1 md:col-span-2">
            <div className="max-w-xl">
              {MENU_DATA.indonesian.map((item, idx) => (
                <MenuItem key={idx} {...item} />
              ))}
            </div>
          </TabsContent>
          
          <TabsContent value="coffee" className="mt-0 col-span-1 md:col-span-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12">
              <div>
                {MENU_DATA.coffee.slice(0, 2).map((item, idx) => (
                  <MenuItem key={idx} {...item} />
                ))}
              </div>
              <div>
                {MENU_DATA.coffee.slice(2).map((item, idx) => (
                  <MenuItem key={idx} {...item} />
                ))}
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="tea" className="mt-0 col-span-1 md:col-span-2">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12">
              <div>
                {MENU_DATA.tea.slice(0, 2).map((item, idx) => (
                  <MenuItem key={idx} {...item} />
                ))}
              </div>
              <div>
                {MENU_DATA.tea.slice(2).map((item, idx) => (
                  <MenuItem key={idx} {...item} />
                ))}
              </div>
            </div>
          </TabsContent>
        </div>
      </Tabs>
    </div>
  </section>
);

// Star Rating Component
const StarRating = ({ rating }) => (
  <div className="flex items-center gap-1">
    {[...Array(5)].map((_, i) => (
      <Star 
        key={i} 
        size={16} 
        className={i < rating ? "star-filled fill-current" : "star-empty"} 
      />
    ))}
  </div>
);

// Testimonials Section
const TestimonialsSection = () => (
  <section 
    id="testimonials" 
    className="py-24 md:py-32 bg-[#0A0A0A]"
    data-testid="testimonials-section"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      {/* Header */}
      <div className="text-center mb-12">
        <span className="text-sm font-body uppercase tracking-[0.2em] text-gold">
          Reviews
        </span>
        <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-gold mt-4 mb-4">
          Apa Kata Mereka
        </h2>
        <div className="flex items-center justify-center gap-3 mt-6">
          <div className="flex items-center gap-1">
            {[...Array(5)].map((_, i) => (
              <Star 
                key={i} 
                size={24} 
                className={i < 4 ? "star-filled fill-current" : "star-empty"} 
              />
            ))}
          </div>
          <span className="text-2xl font-heading text-white">4.4</span>
          <span className="text-gray-500">dari Google Maps</span>
        </div>
      </div>
      
      {/* Testimonial Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {TESTIMONIALS.map((testimonial, idx) => (
          <div 
            key={idx}
            className="dark-card p-6 md:p-8"
            data-testid={`testimonial-card-${idx}`}
          >
            <StarRating rating={testimonial.rating} />
            <p className="testimonial-quote text-gray-300 mt-4 mb-6 leading-relaxed">
              {testimonial.text}
            </p>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-gold/20 flex items-center justify-center">
                <span className="text-gold font-semibold">{testimonial.name.charAt(0)}</span>
              </div>
              <span className="text-white font-medium">{testimonial.name}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// Location Section
const LocationSection = () => (
  <section 
    id="location" 
    className="py-24 md:py-32 bg-[#151515]"
    data-testid="location-section"
  >
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      {/* Header */}
      <div className="text-center mb-12">
        <span className="text-sm font-body uppercase tracking-[0.2em] text-gold">
          Visit Us
        </span>
        <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-gold mt-4">
          Lokasi & Jam Buka
        </h2>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8">
        {/* Info */}
        <div className="md:col-span-4 flex flex-col justify-center">
          <div className="dark-card p-6 md:p-8 mb-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center flex-shrink-0">
                <Clock className="text-gold" size={24} />
              </div>
              <div>
                <h3 className="font-heading text-xl text-white mb-2">Jam Operasional</h3>
                <p className="text-gray-400">Senin - Minggu</p>
                <p className="text-gold font-semibold text-lg">10:00 - 23:00</p>
                <p className="text-gray-500 text-sm mt-2">*Tutup jam 11 malam</p>
              </div>
            </div>
          </div>
          
          <div className="dark-card p-6 md:p-8">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-gold/20 flex items-center justify-center flex-shrink-0">
                <MapPin className="text-gold" size={24} />
              </div>
              <div>
                <h3 className="font-heading text-xl text-white mb-2">Alamat</h3>
                <p className="text-gray-400">8H4Q+M8P</p>
                <p className="text-gray-400">Tarakan Barat</p>
                <p className="text-gray-400">Kalimantan Utara</p>
                <a 
                  href={GOOGLE_MAPS_LINK}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-gold mt-3 hover:underline"
                  data-testid="location-directions-link"
                >
                  Buka di Google Maps
                  <ChevronRight size={16} />
                </a>
              </div>
            </div>
          </div>
        </div>
        
        {/* Map */}
        <div className="md:col-span-8">
          <div className="relative h-[400px] md:h-full min-h-[400px] overflow-hidden border border-gold/20">
            <iframe
              src="https://maps.app.goo.gl/Rfui3e21GP3kTihE7"
              width="100%"
              height="100%"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="The Uncle Coffee & Kitchen Location"
              className="map-dark"
              data-testid="google-map-iframe"
            />
          </div>
        </div>
      </div>
    </div>
  </section>
);

// CTA Section
const CTASection = () => (
  <section 
    id="cta" 
    className="py-24 md:py-32 bg-[#0A0A0A] relative overflow-hidden"
    data-testid="cta-section"
  >
    {/* Background decoration */}
    <div className="absolute inset-0 opacity-5">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-gold blur-3xl" />
    </div>
    
    <div className="max-w-4xl mx-auto px-6 md:px-12 text-center relative z-10">
      <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-gold mb-6">
        Siap untuk Hangout?
      </h2>
      <p className="text-lg text-gray-400 mb-10 max-w-2xl mx-auto">
        Kunjungi kami sekarang atau hubungi untuk reservasi. Tim kami siap melayani dengan sepenuh hati.
      </p>
      
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <a 
          href={GOOGLE_MAPS_LINK}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-primary text-lg"
          data-testid="cta-directions-btn"
        >
          <MapPin size={20} />
          Petunjuk Arah
        </a>
        <a 
          href={`https://wa.me/${WHATSAPP_NUMBER.replace(/\+/g, '')}?text=Halo,%20saya%20ingin%20bertanya%20tentang%20The%20Uncle%20Coffee%20%26%20Kitchen`}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-secondary text-lg"
          data-testid="cta-whatsapp-btn"
        >
          <Phone size={20} />
          Hubungi via WhatsApp
        </a>
      </div>
    </div>
  </section>
);

// Footer
const Footer = () => (
  <footer className="py-12 bg-[#151515] border-t border-gold/20" data-testid="footer">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <div className="flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="flex items-center gap-4">
          <img 
            src={IMAGES.logo} 
            alt="The Uncle Coffee & Kitchen" 
            className="h-10 w-auto"
          />
          <div>
            <span className="text-white font-heading text-lg block">The Uncle</span>
            <span className="text-gray-500 text-sm">Coffee & Kitchen</span>
          </div>
        </div>
        
        <div className="flex items-center gap-6 text-gray-500 text-sm">
          <a href="#about" className="hover:text-gold transition-colors">About</a>
          <a href="#menu" className="hover:text-gold transition-colors">Menu</a>
          <a href="#location" className="hover:text-gold transition-colors">Location</a>
        </div>
        
        <div className="text-gray-500 text-sm text-center md:text-right">
          <p>&copy; {new Date().getFullYear()} The Uncle Coffee & Kitchen</p>
          <p>EST. 2018 • Tarakan, Kalimantan Utara</p>
        </div>
      </div>
    </div>
  </footer>
);

// Floating WhatsApp Button
const WhatsAppFloat = () => (
  <a
    href={`https://wa.me/${WHATSAPP_NUMBER.replace(/\+/g, '')}?text=Halo,%20saya%20ingin%20bertanya%20tentang%20The%20Uncle%20Coffee%20%26%20Kitchen`}
    target="_blank"
    rel="noopener noreferrer"
    className="whatsapp-float"
    aria-label="Chat via WhatsApp"
    data-testid="whatsapp-float"
  >
    <svg viewBox="0 0 24 24" width="32" height="32" fill="white">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  </a>
);

// Main App Component
function App() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
    document.body.style.overflow = !isMobileMenuOpen ? 'hidden' : 'auto';
  };

  return (
    <div className="App bg-[#0A0A0A] min-h-screen">
      <NavBar 
        isScrolled={isScrolled} 
        toggleMobileMenu={toggleMobileMenu}
        isMobileMenuOpen={isMobileMenuOpen}
      />
      <MobileMenu isOpen={isMobileMenuOpen} onClose={toggleMobileMenu} />
      
      <main>
        <HeroSection />
        <AboutSection />
        <MenuSection />
        <TestimonialsSection />
        <LocationSection />
        <CTASection />
      </main>
      
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}

export default App;
